import React from "react";
import styled from "styled-components";
import { NavLink } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useBillingAccounts } from "../../hooks/useBillingAccounts";
import { AttentionTone, BadgeSummary, badgeSummary } from "../../pages/manageSubscriptionModel";
import { typographyTokens, borderRadiusTokens, spacingTokens, animationTokens } from "../../config/designTokens";

// The sidebar's "Manage subscription" item (PRD 114 §4.4): a single link to
// /subscription, the one place a person manages what they pay Greenback for.
//
// THE PILL SAYS WHAT YOU HOLD (BIZ, PRO, FREE), as it always has; the title
// names the plan, Greenback Business or Greenback Pro, never "Free Trial". THE
// SUBTITLE SAYS WHAT NEEDS YOU, first rule first: anything the person can fix
// across their personal Pro, the groups they own and the practice they own
// (with a dot, and named when it is not their own Pro: "Smith CPA: payment
// failed"); a personal cancel already scheduled ("Ends"); Pro that comes with
// Business; free until a date; renewing; free. The server applies the
// visibility rules before anything reaches the list, so a practice's failed
// card never shows on its clients' badges.
//
// WHERE IT GOES: the first account needing attention, `?account=` only.
// Never `&task=`: clicking the menu item to look must open nothing.
//
// HEARD AS WELL AS SEEN (PRD 114 §4.4). The link's name starts with a
// visually hidden "Manage subscription" and goes on with what it shows (the
// pill, the plan, the subtitle), e.g. "Manage subscription, BIZ Greenback
// Business, Payment failed". No aria-label replaces that text: the status
// would be hidden from a screen reader, and a voice user saying the words on
// screen would match nothing (WCAG 2.5.3, label in name). Collapsed, the plan
// and the subtitle stay in the DOM, visually hidden, beside the pill. The dot
// carries a glyph, so red and amber are not told apart by colour alone, and
// the tooltip repeats what needs attention. On a phone the badge sits in the
// closed drawer, so SubscriptionAttentionLink puts the dot in the header.
//
// Without the list (an api that does not serve it yet) the subtitle reads
// from the Subscription alone, as it always did.

const LABEL = "Manage subscription";

interface SubscriptionBadgeProps {
  isCollapsed?: boolean;
}

/** BIZ supersedes PRO. Older API responses omit business_entitled — the strict === true treats it as false. */
const holdsBusiness = (isPro: boolean | undefined, business_entitled: boolean | undefined): boolean =>
  isPro === true && business_entitled === true;

/** The badge's subtitle, dot and link for the signed-in person. */
const useBadgeSummary = (): BadgeSummary | null => {
  const { user, isPro } = useAuth();
  const billing = useBillingAccounts();
  if (!user) return null;
  const data = billing.status === "ready" ? billing.data : null;
  return badgeSummary({
    subscription: user.subscription,
    isPro: isPro === true,
    isBusiness: holdsBusiness(isPro, user.subscription?.business_entitled),
    data,
  });
};

export const SubscriptionBadge: React.FC<SubscriptionBadgeProps> = ({ isCollapsed = false }) => {
  const { user, isPro } = useAuth();
  const summary = useBadgeSummary();

  if (!user || !summary) return null;
  const subscription = user.subscription;

  const isGrandfathered =
    subscription?.grandfathered_until &&
    new Date(subscription.grandfathered_until) > new Date();

  // Business includes Pro, so BIZ supersedes PRO in display.
  const isBusiness = holdsBusiness(isPro, subscription?.business_entitled);

  // Never "Free Trial": the grandfathered window is also a comp, the seat a
  // Business group bundles, and what is left of a paused group's seat, none of
  // them a trial. The pill says FREE; the subtitle says until when.
  const title = isBusiness ? "Greenback Business" : "Greenback Pro";
  const tooltip = summary.needsAttention ? `${LABEL}: ${summary.subtitle}` : LABEL;

  return (
    <BadgeContainer to={summary.to} $isCollapsed={isCollapsed} title={tooltip}>
      {/* The name's first words; what follows is the text on screen. */}
      <VisuallyHidden>{`${LABEL}, `}</VisuallyHidden>
      <BadgeRow>
        <PillWrap>
          <Pill $variant={isBusiness ? "biz" : isPro ? (isGrandfathered ? "grandfathered" : "pro") : "free"}>
            {isBusiness ? "BIZ" : isPro ? (isGrandfathered ? "FREE" : "PRO") : "FREE"}
          </Pill>
          {summary.tone && <AttentionDot $tone={summary.tone} $overlay aria-hidden="true">!</AttentionDot>}
        </PillWrap>
        {/* The rest of the name: the visible plan and subtitle, or the same
            words hidden when collapsed and only the pill shows. */}
        {isCollapsed ? (
          <VisuallyHidden>{[title, summary.subtitle].filter(Boolean).join(", ")}</VisuallyHidden>
        ) : (
          <BadgeText>
            <BadgeTitle>{title}</BadgeTitle>
            {summary.subtitle && (
              <>
                <VisuallyHidden>, </VisuallyHidden>
                <BadgeSubtitle $attention={summary.needsAttention}>{summary.subtitle}</BadgeSubtitle>
              </>
            )}
          </BadgeText>
        )}
      </BadgeRow>
    </BadgeContainer>
  );
};

/**
 * The phone header's mark: the badge lives in the drawer, which starts
 * closed, so something that needs the person shows here too. A 24px spacer
 * otherwise, which keeps the header's title centred as before.
 */
export const SubscriptionAttentionLink: React.FC = () => {
  const summary = useBadgeSummary();

  if (!summary?.needsAttention || !summary.tone) return <HeaderSlot data-testid="mobile-header-spacer" />;

  return (
    <HeaderLink
      to={summary.to}
      aria-label={`${LABEL}: ${summary.subtitle}`}
      title={`${LABEL}: ${summary.subtitle}`}
    >
      <AttentionDot $tone={summary.tone} aria-hidden="true">!</AttentionDot>
    </HeaderLink>
  );
};

const BadgeContainer = styled(NavLink)<{ $isCollapsed?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: ${({ $isCollapsed }) => ($isCollapsed ? "center" : "flex-start")};
  padding: ${({ $isCollapsed }) => $isCollapsed ? `${spacingTokens.md} ${spacingTokens.xs}` : `${spacingTokens.md} ${spacingTokens.xxl}`};
  text-decoration: none;
  border-radius: ${borderRadiusTokens.md};
  transition: background-color ${animationTokens.duration.short} ${animationTokens.easing.easeInOut};
  margin: ${({ $isCollapsed }) => $isCollapsed ? `${spacingTokens.sm} ${spacingTokens.xs}` : `${spacingTokens.sm} ${spacingTokens.md}`};

  &:hover {
    background-color: ${({ theme }) => theme.isDarkMode ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"};
  }
`;

const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const PillWrap = styled.span`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
`;

/**
 * Red for money that did not arrive, amber for a state to act on, with a
 * glyph so the two are not told apart by colour alone. The glyph's colour is
 * the one that reads on that fill in each theme.
 */
const AttentionDot = styled.span<{ $tone: AttentionTone; $overlay?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: ${borderRadiusTokens.full};
  font-family: ${typographyTokens.fontFamily.bold};
  font-size: 10px;
  font-weight: ${typographyTokens.fontWeight.bold};
  line-height: 1;
  background-color: ${({ $tone, theme }) => ($tone === "negative" ? theme.semantic.negative : theme.semantic.warning)};
  color: ${({ $tone, theme }) =>
    !theme.isDarkMode && $tone === "warning" ? theme.text.primary : theme.background.paper};
  ${({ $overlay }) => ($overlay ? "position: absolute; top: -6px; right: -6px;" : "")}
`;

/** The same footprint as the spacer it replaces in the phone header. */
const headerSlotCss = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  text-decoration: none;
`;

const HeaderSlot = styled.div`${headerSlotCss}`;
const HeaderLink = styled(NavLink)`${headerSlotCss}`;

// Not overflow: hidden, which would clip the dot overlapping the pill's
// corner: min-width: 0 lets the row shrink, and BadgeText clips the text.
const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacingTokens.md};
  min-width: 0;
`;

const Pill = styled.span<{ $variant: "biz" | "pro" | "grandfathered" | "free" }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: ${borderRadiusTokens.full};
  font-family: ${typographyTokens.fontFamily.bold};
  font-size: 10px;
  font-weight: ${typographyTokens.fontWeight.bold};
  letter-spacing: 0.06em;
  background-color: ${({ $variant, theme }) =>
    $variant === "biz"
      ? theme.brand.primary
      : $variant === "pro"
      ? theme.text.primary
      : $variant === "grandfathered"
      ? (theme.isDarkMode ? "#1d4ed8" : "#2563eb")
      : (theme.isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)")};
  color: ${({ $variant, theme }) =>
    $variant === "biz"
      ? theme.text.onBrand
      : $variant === "pro"
      ? theme.background.paper
      : $variant === "grandfathered"
      ? "#ffffff"
      : theme.text.secondary};
`;

const BadgeText = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const BadgeTitle = styled.span`
  font-family: ${typographyTokens.fontFamily.medium};
  font-size: ${typographyTokens.fontSize.body};
  font-weight: ${typographyTokens.fontWeight.medium};
  color: ${({ theme }) => theme.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BadgeSubtitle = styled.span<{ $attention?: boolean }>`
  font-family: ${typographyTokens.fontFamily.primary};
  font-size: ${typographyTokens.fontSize.caption};
  color: ${({ theme, $attention }) => ($attention ? theme.text.primary : theme.text.secondary)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
